import React, { useMemo } from 'react';

const isPresent = (value) =>
  value !== null && value !== undefined && value !== '';

const numericValue = (value) => {
  if (!isPresent(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value, digits) => {
  const parsed = numericValue(value);
  return parsed === null ? 'N/A' : parsed.toFixed(digits);
};

const displayValue = (value) => isPresent(value) ? value : 'N/A';

export const consolidateCveEntries = (entries = []) => {
  const byCve = new Map();

  for (const entry of Array.isArray(entries) ? entries : []) {
    const cveId = String(entry?.cveId || '').trim().toUpperCase();
    if (!cveId) continue;

    const existing = byCve.get(cveId) || { cveId };
    const merged = { ...existing };

    for (const field of [
      'url',
      'baseSeverity',
      'exploitability',
      'impact',
      'confidentiality',
      'integrity',
      'availability',
      'baseScore',
      'remediation',
      'detectedBy'
    ]) {
      if (!isPresent(merged[field]) && isPresent(entry?.[field])) {
        merged[field] = entry[field];
      }
    }

    const incomingEpss = numericValue(entry?.epss);
    const existingEpss = numericValue(merged.epss);
    if (incomingEpss !== null && (existingEpss === null || incomingEpss > existingEpss)) {
      merged.epss = incomingEpss;
    }

    byCve.set(cveId, merged);
  }

  return [...byCve.values()].sort((a, b) => {
    const epssDifference = (numericValue(b.epss) || 0) - (numericValue(a.epss) || 0);
    return epssDifference || a.cveId.localeCompare(b.cveId);
  });
};

const CveEpssTable = ({ entries, onCveClick }) => {
  const rows = useMemo(() => consolidateCveEntries(entries), [entries]);

  if (!rows.length) {
    return (
      <div className="cve-epss-empty">
        <i className="bi bi-info-circle" />
        <h5>No verified CVEs found</h5>
        <p>
          This scan contains security findings, but none are linked to a verified CVE ID.
          EPSS and CVSS details are only available for published CVEs.
        </p>
      </div>
    );
  }

  return (
    <div className="cve-epss-table-wrap">
      <table className="table table-dark table-striped table-hover align-middle cve-epss-table">
        <thead>
          <tr>
            <th>CVE ID</th>
            <th>EPSS</th>
            <th>Severity</th>
            <th>Exploitability</th>
            <th>Impact</th>
            <th>Confidentiality</th>
            <th>Integrity</th>
            <th>Availability</th>
            <th>Base Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((cve) => (
            <tr key={cve.cveId}>
              <td>
                <button
                  type="button"
                  className="cve-epss-id"
                  onClick={() => onCveClick?.(cve)}
                  title="Open security decision options"
                >
                  {cve.cveId}
                </button>
              </td>
              <td>{formatNumber(cve.epss, 6)}</td>
              <td>{displayValue(cve.baseSeverity)}</td>
              <td>{formatNumber(cve.exploitability, 2)}</td>
              <td>{formatNumber(cve.impact, 2)}</td>
              <td>{displayValue(cve.confidentiality)}</td>
              <td>{displayValue(cve.integrity)}</td>
              <td>{displayValue(cve.availability)}</td>
              <td>{formatNumber(cve.baseScore, 1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CveEpssTable;
